import { useState, useEffect, useRef, useCallback } from "react";
import {
  App as DwvApp,
  AppOptions,
  ViewConfig,
  ToolConfig,
  Index,
} from "dwv";
// import AnonymizeDialog from "./AnonymizeDialog";
import ExportVideoDialog from "./ExportVideoDialog";
import {
  Download, Loader2, Maximize, Minimize, Play, Pause, ChevronLeft, ChevronRight, SkipBack,
} from "lucide-react";
import ExportFramesDialog from "./ExportFramesDialog";
import FrameFilmstrip from "./FrameFilmstrip";
import { getCachedFrameThumbnail, clearFrameThumbnailCache } from "@/lib/dwvFrameThumbnail";
import { getStoredToken } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Auth headers for DWV XHR loads (axios interceptors do not apply). */
const getDwvRequestOptions = () => {
  const token = getStoredToken();
  if (!token) return undefined;
  return {
    requestHeaders: [{ name: "Authorization", value: `Bearer ${token}` }],
  };
};

/**
 * Playback axis and frame count of the loaded view.
 *
 * Cine (fluoro) data keeps its frames outside the slice dimension, so dwv's scroll
 * dim can report a single slice while the frames live on a later axis. Backend
 * counts can also overcount; seeking past the real extent renders a blank canvas.
 */
const getPlaybackGeometry = (vc, fallbackDim) => {
  if (!vc) return null;

  let size = null;
  try { size = vc.getImageSize?.(); } catch (_) {}
  if (typeof size?.get !== "function") return null;

  let dims = 3;
  try {
    const idx = vc.getCurrentIndex?.();
    if (typeof idx?.length === "function") dims = idx.length();
  } catch (_) {}

  let scrollDim = null;
  try {
    if (typeof vc.getScrollDimIndex === "function") scrollDim = vc.getScrollDimIndex();
  } catch (_) {}

  const sizeAt = (d) => {
    try {
      const n = size.get(d);
      return Number.isFinite(n) ? n : 0;
    } catch (_) { return 0; }
  };

  let dim = Number.isInteger(scrollDim) ? scrollDim : (fallbackDim ?? 2);
  if (sizeAt(dim) <= 1) {
    for (let d = 2; d < dims; d++) {
      if (sizeAt(d) > 1) { dim = d; break; }
    }
  }

  const total = sizeAt(dim);
  if (total < 1) return null;
  return { dim, total, usesScrollApi: dim === scrollDim };
};

/** Current frame on the playback axis. */
const readPlaybackFrame = (vc, geo) => {
  try {
    if (geo?.usesScrollApi && typeof vc.getCurrentPositionScrollValue === "function") {
      const v = vc.getCurrentPositionScrollValue();
      if (Number.isFinite(v)) return v;
    }
    const values = vc.getCurrentIndex?.()?.getValues?.();
    const v = values?.[geo?.dim ?? 2];
    return Number.isFinite(v) ? v : 0;
  } catch (_) { return 0; }
};

/** Sync scroll axis + frame count from the active view (updates after MPR reorient). */
const syncScrollFromViewController = (vc, setters) => {
  if (!vc) return;
  try {
    if (typeof vc.getScrollDimIndex === "function") {
      setters.setScrollDim(vc.getScrollDimIndex());
    }
    let nf = 1;
    if (typeof vc.getMaximumScrollValue === "function") {
      const max = vc.getMaximumScrollValue();
      if (Number.isFinite(max) && max >= 0) nf = max + 1;
    }
    if (nf > 1) {
      setters.setHasMultiFrame(true);
      setters.setTotalFrames(nf);
      setters.setTotalFramesRef(nf);
    }
    let frameVal;
    if (typeof vc.getCurrentPositionScrollValue === "function") {
      frameVal = vc.getCurrentPositionScrollValue();
    } else if (typeof vc.getCurrentIndexScrollValue === "function") {
      frameVal = vc.getCurrentIndexScrollValue();
    }
    if (frameVal !== undefined && Number.isFinite(frameVal)) {
      setters.setCurrentFrame(frameVal);
    }
  } catch (_) {}
};

export default function DicomViewer({
  fileUrl,
  volumeUrls,
  onMetadataLoaded,
  knownNumFrames,
  fileId,
  fileName,
  patientName,
  seriesSliceIdx,
  exportVideo = false,
  fullscreenRef: externalFullscreenRef,
  hideFilmstrip = false,
  hideDrawTools = false,
  autoPlayCine = false,
  hideLoadingOverlay = false,
  /** Parent is still fetching the DICOM blob/URL (e.g. fluoro download). */
  awaitingSource = false,
  seriesScrollEnabled = false,
  onSeriesScroll,
  frameIndex,
  onFrameChange,
  onScrollMetaChange,
  onSeekApplied,
  /** Target orientation when a volume is loaded: 'axial' | 'coronal' | 'sagittal' */
  externalOrientation,
  hideOrientationTool = false,
  hidePlaybackBar = false,
  bottomBar = null,
  onDecodeStart,
  onDecodeReady,
}) {
  const rootRef = useRef(null);
  const fullscreenRef = externalFullscreenRef ?? rootRef;
  const containerRef = useRef(null);
  const dwvAppRef = useRef(null);
  const vcRef = useRef(null);
  const hideLoadingOverlayRef = useRef(hideLoadingOverlay);
  const seriesScrollEnabledRef = useRef(seriesScrollEnabled);
  const onSeriesScrollRef = useRef(onSeriesScroll);
  const onDecodeStartRef = useRef(onDecodeStart);
  const onDecodeReadyRef = useRef(onDecodeReady);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [activeTool, setActiveTool] = useState("Scroll");
  const [activeShape, setActiveShape] = useState("Ruler");
  const activeToolRef = useRef("Scroll");
  const activeShapeRef = useRef("Ruler");
  const [metadata, setMetadata] = useState(null);
  const [showTags, setShowTags] = useState(false);
  const [canWindowLevel, setCanWindowLevel] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [orientation, setOrientation] = useState("axial");
  const [presetNames, setPresetNames] = useState([]);
  // Cine state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(1);
  // Frames the loaded view can actually scroll through — trusted over backend counts.
  const [viewExtent, setViewExtent] = useState(null);
  const [fps, setFps] = useState(15);
  const [hasMultiFrame, setHasMultiFrame] = useState(false);
  const [forceShowCine, setForceShowCine] = useState(false);
  // const [anonOpen, setAnonOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [selectedFrames, setSelectedFrames] = useState(() => new Set());
  const [viewerReady, setViewerReady] = useState(false);
  const scrollDimRef = useRef(2);
  const cineIntervalRef = useRef(null);
  const totalFramesRef = useRef(1);
  const isFirstLoad = useRef(true);
  const resizeRafRef = useRef(null);
  const loadTokenRef = useRef(0);
  // Parent passes volumeUrls only when Coronal/Sagittal is requested.
  const volumeMode = Array.isArray(volumeUrls) && volumeUrls.length > 1;
  const volumeKey = volumeMode ? `${fileId || "vol"}:${volumeUrls.length}` : null;
  const loadDep = volumeMode ? `vol:${volumeKey}` : `file:${fileUrl || ""}`;

  const shapeNames = ["Ruler", "Arrow", "Rectangle", "Circle", "Ellipse", "Protractor", "Roi"];
  const tools = {
    Scroll: new ToolConfig(),
    ZoomAndPan: new ToolConfig(),
    WindowLevel: new ToolConfig(),
    Draw: new ToolConfig(shapeNames),
  };

  useEffect(() => {
    hideLoadingOverlayRef.current = hideLoadingOverlay;
  }, [hideLoadingOverlay]);

  useEffect(() => {
    seriesScrollEnabledRef.current = seriesScrollEnabled;
  }, [seriesScrollEnabled]);

  useEffect(() => {
    onSeriesScrollRef.current = onSeriesScroll;
  }, [onSeriesScroll]);

  useEffect(() => {
    onDecodeStartRef.current = onDecodeStart;
  }, [onDecodeStart]);

  useEffect(() => {
    onDecodeReadyRef.current = onDecodeReady;
  }, [onDecodeReady]);

  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  useEffect(() => {
    activeShapeRef.current = activeShape;
  }, [activeShape]);

  useEffect(() => {
    if (!containerRef.current) return;

    const layerGroupName = "dwv-layerGroup";
    const viewConfig = new ViewConfig(layerGroupName);
    const viewConfigs = { "*": [viewConfig] };
    const options = new AppOptions(viewConfigs);
    options.tools = tools;

    const app = new DwvApp();
    app.init(options);
    dwvAppRef.current = app;

    app.addEventListener("loadstart", () => {
      setLoading(true);
      // Keep previous frame visible during series scroll/play — avoid black flash + loader.
      if (!hideLoadingOverlayRef.current) {
        setLoaded(false);
      }
      setLoadProgress(0);
      setLoadError(null);
      // NOTE: don't reset hasMultiFrame here — it's seeded from
      // knownNumFrames via a separate effect that runs on fileUrl change.
      // Resetting here would race that seed and hide the cine controls.
      setCurrentFrame(0);
      if (!hideLoadingOverlayRef.current) {
        setIsPlaying(false);
        isFirstLoad.current = true;
      }
      setViewerReady(false);
      vcRef.current = null;
      if (cineIntervalRef.current) { clearInterval(cineIntervalRef.current); cineIntervalRef.current = null; }
      onDecodeStartRef.current?.();
    });

    app.addEventListener("loadprogress", (event) => {
      setLoadProgress(event.loaded);
    });

    // Always update VC and detect multi-frame on every renderend
    app.addEventListener("renderend", (event) => {
      try {
        if (!dwvAppRef.current) return;
        const vl = app.getViewLayersByDataId(event.dataid)[0];
        if (!vl) return;
        const vc = vl.getViewController();
        vcRef.current = vc;
        setViewerReady(true);
        setCanWindowLevel(vc.isMonochrome());
        setCanScroll(vc.canScroll());
        setPresetNames(vc.getWindowLevelPresetsNames());

        // Detect multi-frame from image geometry. Prefer view scroll APIs so
        // coronal/sagittal MPR updates the correct axis and slice count.
        syncScrollFromViewController(vc, {
          setScrollDim: (d) => { scrollDimRef.current = d; },
          setHasMultiFrame,
          setTotalFrames,
          setTotalFramesRef: (n) => { totalFramesRef.current = n; },
          setCurrentFrame,
        });
        const geo = getPlaybackGeometry(vc, scrollDimRef.current);
        if (geo && geo.total > 1) {
          scrollDimRef.current = geo.dim;
          setViewExtent(geo.total);
          setHasMultiFrame(true);
        }
        try {
          const img = app.getImage(event.dataid);
          if (img && totalFramesRef.current <= 1) {
            const size = img.getGeometry().getSize();
            const can3D = size.canScroll3D ? size.canScroll3D() : false;
            let playbackDim = typeof vc.getScrollDimIndex === "function"
              ? vc.getScrollDimIndex()
              : 2;
            const idx = vc.getCurrentIndex();
            const dims = idx ? idx.length() : 3;
            if (!can3D) {
              for (let d = 2; d < dims; d++) {
                if (size.get(d) > 1) { playbackDim = d; break; }
              }
              if (size.get(playbackDim) <= 1 && dims > 3) playbackDim = 3;
            }
            scrollDimRef.current = playbackDim;
            const nf = size.get(playbackDim);
            if (nf > 1) {
              setHasMultiFrame(true);
              setTotalFrames(nf);
              totalFramesRef.current = nf;
            }
            try {
              if (idx && playbackDim < idx.length()) {
                const frameVal = idx.get(playbackDim);
                if (frameVal !== undefined) setCurrentFrame(frameVal);
              }
            } catch (e) {}
          }
        } catch (imgErr) {
          // getImage may fail during early renders, will retry on load
        }

        // Re-apply the user's tool after each render/load. Slice changes call
        // app.reset() which drops the active tool — without this, Scroll snaps
        // back to ZoomAndPan on every wheel step.
        const scrollAvailable = vc.canScroll() || seriesScrollEnabledRef.current;
        if (isFirstLoad.current) {
          // Prefer Scroll by default when the study can scroll; otherwise Zoom/Pan.
          const defaultTool = scrollAvailable ? "Scroll" : "ZoomAndPan";
          try {
            if (defaultTool === "Scroll" && seriesScrollEnabledRef.current && !vc.canScroll()) {
              try {
                app.setTool("Scroll");
              } catch (_) {
                try { app.setTool("ZoomAndPan"); } catch (__) {}
              }
            } else {
              app.setTool(defaultTool);
            }
            setActiveTool(defaultTool);
            activeToolRef.current = defaultTool;
          } catch (_) {
            try {
              app.setTool("ZoomAndPan");
              setActiveTool("ZoomAndPan");
              activeToolRef.current = "ZoomAndPan";
            } catch (__) {}
          }
          isFirstLoad.current = false;
        } else {
          const restoreTool = activeToolRef.current || (scrollAvailable ? "Scroll" : "ZoomAndPan");
          try {
            if (restoreTool === "Draw") {
              app.setTool("Draw");
              app.setToolFeatures({ shapeName: activeShapeRef.current || "Ruler" });
            } else if (restoreTool === "Scroll" && seriesScrollEnabledRef.current && !vc.canScroll()) {
              try {
                app.setTool("Scroll");
              } catch (_) {
                try { app.setTool("ZoomAndPan"); } catch (__) {}
              }
            } else {
              app.setTool(restoreTool);
            }
          } catch (_) {}
        }
      } catch (e) {
        console.error("renderend error:", e);
      }
    });

    app.addEventListener("load", (event) => {
      if (!dwvAppRef.current) return;
      setLoading(false);
      setLoaded(true);
      setLoadProgress(100);
      try {
        const meta = structuredClone(app.getMetaData(event.dataid));
        const pixelDataKey = "7FE00010";
        if (meta[pixelDataKey]) delete meta[pixelDataKey];
        setMetadata(meta);
        if (onMetadataLoaded) onMetadataLoaded(meta);

        // Fallback multi-frame detection from metadata
        let nFrames = 1;
        const nfTag = meta["00280008"];
        if (nfTag != null) {
          let raw = nfTag;
          if (typeof raw === "object" && raw !== null && raw.value !== undefined) {
            raw = Array.isArray(raw.value) ? raw.value[0] : raw.value;
          }
          const parsed = parseInt(String(raw), 10);
          if (!isNaN(parsed) && parsed > 1) nFrames = parsed;
        }
        // Also try image geometry (VC should be set by now)
        if (nFrames <= 1) {
          try {
            const img = app.getImage(event.dataid);
            if (img) {
              const size = img.getGeometry().getSize();
              const dim = scrollDimRef.current;
              const dimSize = size.get(dim);
              if (dimSize > 1) nFrames = dimSize;
            }
          } catch (e) {}
        }
        if (nFrames > 1) {
          setHasMultiFrame(true);
          setTotalFrames(nFrames);
          totalFramesRef.current = nFrames;
          setCurrentFrame(0);
        }
      } catch (e) {
        console.error("load error:", e);
      }
      onDecodeReadyRef.current?.();
    });

    app.addEventListener("loaderror", (event) => {
      console.error("DICOM load error:", event.error);
      setLoading(false);
      setLoadError(event.error?.message || "Failed to parse DICOM file");
      onDecodeReadyRef.current?.();
    });

    app.addEventListener("loadend", () => {
      setLoading(false);
    });

    app.addEventListener("keydown", (event) => {
      app.defaultOnKeydown(event);
    });

    // Track position changes for frame counter (orientation-aware scroll axis)
    app.addEventListener("positionchange", () => {
      const vc = vcRef.current;
      if (!vc) return;
      try {
        if (typeof vc.getScrollDimIndex === "function") {
          scrollDimRef.current = vc.getScrollDimIndex();
        }
        let frameVal;
        if (typeof vc.getCurrentPositionScrollValue === "function") {
          frameVal = vc.getCurrentPositionScrollValue();
        } else {
          frameVal = vc.getCurrentIndex()?.get(scrollDimRef.current);
        }
        if (frameVal !== undefined) setCurrentFrame(frameVal);
      } catch (e) {}
    });

    const safeResize = () => {
      const el = containerRef.current;
      if (!el || !dwvAppRef.current) return;
      const { clientWidth, clientHeight } = el;
      if (clientWidth <= 0 || clientHeight <= 0) return;
      try {
        dwvAppRef.current.onResize();
      } catch (e) {
        // DWV fitToContainer can throw when canvas is 0×0 during flex reflow
        console.warn("DWV resize skipped:", e);
      }
    };

    const scheduleResize = () => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        safeResize();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    window.addEventListener("resize", scheduleResize);

    return () => {
      window.removeEventListener("resize", scheduleResize);
      resizeObserver.disconnect();
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      if (cineIntervalRef.current) clearInterval(cineIntervalRef.current);
      loadTokenRef.current += 1;
      try { app.abortAllLoads?.(); } catch (_) {}
      try { app.reset(); } catch (e) {}
      dwvAppRef.current = null;
      vcRef.current = null;
    };
  }, []);

  useEffect(() => {
    const app = dwvAppRef.current;
    if (!app) return undefined;

    if (cineIntervalRef.current) {
      clearInterval(cineIntervalRef.current);
      cineIntervalRef.current = null;
    }
    setIsPlaying(false);
    setOrientation(volumeMode ? (externalOrientation || "axial") : "axial");
    isFirstLoad.current = true;
    loadTokenRef.current += 1;
    const token = loadTokenRef.current;

    try { app.abortAllLoads?.(); } catch (_) {}
    try { app.abortLoad?.(); } catch (_) {}

    try {
      app.reset();
      // Always decode in acquisition plane first; MPR applied after volume load.
      const viewConfig = new ViewConfig("dwv-layerGroup");
      app.setDataViewConfigs({ "*": [viewConfig] });
    } catch (e) {
      console.warn("DWV reset skipped:", e);
    }

    const urls = volumeMode ? volumeUrls : (fileUrl ? [fileUrl] : null);
    if (!urls?.length) {
      setLoading(false);
      setLoaded(false);
      setViewerReady(false);
      return undefined;
    }

    // Show loader immediately — loadstart can lag a tick after loadURLs.
    setLoading(true);
    setLoadError(null);

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled || loadTokenRef.current !== token || !dwvAppRef.current) return;
      try {
        const opts = volumeMode || !String(urls[0]).startsWith("blob:")
          ? getDwvRequestOptions()
          : undefined;
        dwvAppRef.current.loadURLs(urls, opts);
      } catch (e) {
        console.error("Error loading DICOM URL:", e);
        setLoadError(e?.message || "Failed to load DICOM");
        setLoading(false);
      }
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      try { app.abortAllLoads?.(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDep]);

  const applyOrientation = useCallback((nextOri) => {
    if (!dwvAppRef.current || !nextOri) return;
    setOrientation(nextOri);
    try {
      const vcCfg = new ViewConfig("dwv-layerGroup");
      if (nextOri !== "axial") vcCfg.orientation = nextOri;
      dwvAppRef.current.setDataViewConfigs({ "*": [vcCfg] });
      const dataIds = dwvAppRef.current.getDataIds();
      for (const dataId of dataIds) dwvAppRef.current.render(dataId);
      requestAnimationFrame(() => {
        try {
          dwvAppRef.current?.fitToContainer?.();
          dwvAppRef.current?.onResize?.();
        } catch (_) {}
        const vc = vcRef.current;
        if (!vc) return;
        syncScrollFromViewController(vc, {
          setScrollDim: (d) => { scrollDimRef.current = d; },
          setHasMultiFrame,
          setTotalFrames,
          setTotalFramesRef: (n) => { totalFramesRef.current = n; },
          setCurrentFrame,
        });
        setCanScroll(vc.canScroll());
        try {
          const presets = vc.getWindowLevelPresetsNames?.() || [];
          if (presets.length > 0) vc.setWindowLevelPreset(presets[0]);
          setPresetNames(presets);
        } catch (_) {}
      });
    } catch (e) {
      console.error("Orientation apply failed:", e);
      toast.error("Could not switch orientation");
    }
  }, []);

  // After a volume finishes loading (or plane changes while volume is open), apply the plane.
  const appliedOriRef = useRef(null);
  useEffect(() => {
    if (!volumeMode || !loaded || !viewerReady) return;
    const target = externalOrientation || "axial";
    if (appliedOriRef.current === target) return;
    appliedOriRef.current = target;
    applyOrientation(target);
  }, [volumeMode, loaded, viewerReady, externalOrientation, applyOrientation]);

  useEffect(() => {
    appliedOriRef.current = null;
  }, [loadDep]);

  // Seed multi-frame state from backend-known frame count so cine controls
  // appear immediately even before dwv finishes geometry detection.
  useEffect(() => {
    setForceShowCine(false);
    setViewExtent(null);
    if (knownNumFrames && knownNumFrames > 1) {
      setHasMultiFrame(true);
      setTotalFrames(knownNumFrames);
      totalFramesRef.current = knownNumFrames;
    } else {
      setHasMultiFrame(false);
      setTotalFrames(1);
      totalFramesRef.current = 1;
    }
    setCurrentFrame(0);
    setSelectedFrames(new Set());
  }, [loadDep, knownNumFrames]);

  useEffect(() => {
    setSelectedFrames(new Set());
  }, [seriesSliceIdx]);

  useEffect(() => {
    clearFrameThumbnailCache(fileUrl);
  }, [fileUrl, seriesSliceIdx]);

  const getFrameThumbnail = useCallback(async (frameIndex, thumbW = 112, thumbH = 84) => {
    const vc = vcRef.current;
    if (!vc) throw new Error("Viewer not ready");
    return getCachedFrameThumbnail({
      vc,
      scrollDim: scrollDimRef.current,
      frameIndex,
      fileUrl: volumeKey || fileUrl,
      thumbW,
      thumbH,
    });
  }, [fileUrl, volumeKey]);

  // ---- Cine Playback via custom interval + setCurrentIndex ----
  // Native vc.play() ignores our FPS override, so we drive our own
  // interval calling setCurrentIndex(...) to step through frames at the
  // user-configured rate. The "rainbow" colormap bug that broke this path
  // before is fixed by resetting the app between loads (single view layer).
  const setScrollFrame = useCallback((targetFrame) => {
    const vc = vcRef.current;
    if (!vc) return false;
    try {
      const geo = getPlaybackGeometry(vc, scrollDimRef.current);
      const total = geo?.total ?? totalFramesRef.current;
      const clamped = Math.max(0, Math.min(total - 1, targetFrame));
      if (geo) scrollDimRef.current = geo.dim;
      // Orientation-aware scroll APIs are only correct when the frames live on
      // dwv's scroll axis (volumes / MPR); cine frames need the index path.
      if (
        geo?.usesScrollApi !== false
        && typeof vc.getCurrentPositionAtScrollValue === "function"
        && typeof vc.setCurrentPosition === "function"
      ) {
        const pos = vc.getCurrentPositionAtScrollValue(clamped);
        const applied = vc.setCurrentPosition(pos);
        // Track the frame we applied — renderend does not fire for every seek,
        // so the slider/counter would otherwise stay frozen during playback.
        if (applied !== false) setCurrentFrame(clamped);
        return applied;
      }
      const idx = vc.getCurrentIndex();
      const values = idx.getValues();
      const dim = geo?.dim ?? scrollDimRef.current;
      const newValues = values.slice();
      newValues[dim] = clamped;
      vc.setCurrentIndex(new Index(newValues));
      setCurrentFrame(clamped);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const startCine = useCallback(() => {
    const vc = vcRef.current;
    if (!vc) return;
    setIsPlaying(true);
    if (cineIntervalRef.current) clearInterval(cineIntervalRef.current);
    const interval = Math.max(16, Math.round(1000 / fps));
    cineIntervalRef.current = setInterval(() => {
      const curVc = vcRef.current;
      if (!curVc) return;
      try {
        const geo = getPlaybackGeometry(curVc, scrollDimRef.current);
        const total = geo?.total ?? totalFramesRef.current;
        if (total <= 1) return;
        const cur = readPlaybackFrame(curVc, geo);
        const next = (cur + 1) % total;
        setScrollFrame(next);
      } catch (e) {
        // skip a tick on error
      }
    }, interval);
  }, [fps, setScrollFrame]);

  const stopCine = useCallback(() => {
    setIsPlaying(false);
    if (cineIntervalRef.current) {
      clearInterval(cineIntervalRef.current);
      cineIntervalRef.current = null;
    }
  }, []);

  // Use the live interval as the source of truth: a stale isPlaying flag
  // (interval cleared on reload) would otherwise swallow the first click.
  const togglePlay = useCallback(() => {
    if (cineIntervalRef.current) stopCine();
    else startCine();
  }, [startCine, stopCine]);

  // Restart playback when FPS changes while playing (so new rate takes effect)
  useEffect(() => {
    if (isPlaying) {
      const vc = vcRef.current;
      if (vc) {
        try { vc.stop(); } catch (e) {}
        startCine();
      }
    }
    return () => {
      if (cineIntervalRef.current) clearInterval(cineIntervalRef.current);
    };
  }, [fps]);

  const stepFrame = useCallback((forward) => {
    const vc = vcRef.current;
    if (!vc) return;
    try {
      const geo = getPlaybackGeometry(vc, scrollDimRef.current);
      const cur = readPlaybackFrame(vc, geo);
      const total = geo?.total ?? totalFramesRef.current;
      const next = forward
        ? (cur + 1) % total
        : (cur - 1 + total) % total;
      setScrollFrame(next);
    } catch (e) {}
  }, [setScrollFrame]);

  const goToFrame = useCallback((targetFrame) => {
    setScrollFrame(targetFrame);
  }, [setScrollFrame]);

  const goToFirstFrame = useCallback(() => {
    goToFrame(0);
  }, [goToFrame]);

  useEffect(() => {
    if (typeof onFrameChange !== "function") return;
    onFrameChange(currentFrame);
  }, [currentFrame, onFrameChange]);

  // Only notify parent when scroll extent / orientation changes — not every frame
  // (avoids parent re-renders that fight controlled Slider / frameIndex sync).
  useEffect(() => {
    if (typeof onScrollMetaChange !== "function") return;
    const total = totalFrames > 1 ? totalFrames : (knownNumFrames || 1);
    onScrollMetaChange({ total, orientation });
  }, [totalFrames, knownNumFrames, orientation, onScrollMetaChange]);

  // Apply external seeks only when frameIndex itself changes (user slider / play).
  // Parent clears frameIndex via onSeekApplied so viewer scroll does not loop back.
  useEffect(() => {
    if (typeof frameIndex !== "number" || !Number.isFinite(frameIndex)) return undefined;
    goToFrame(frameIndex);
    // Clear on next tick so this effect does not re-enter in the same commit.
    const t = window.setTimeout(() => {
      if (typeof onSeekApplied === "function") onSeekApplied();
    }, 0);
    return () => window.clearTimeout(t);
  }, [frameIndex, goToFrame, onSeekApplied]);

  const selectTool = useCallback((toolName) => {
    if (!dwvAppRef.current || !loaded) return;
    // Multi-file CT series without a loaded volume: Scroll is emulated via wheel.
    // Keep dwv on Scroll (inert for single-slice data) so dragging does not pan.
    if (toolName === "Scroll" && seriesScrollEnabled && !canScroll) {
      activeToolRef.current = "Scroll";
      setActiveTool("Scroll");
      try {
        dwvAppRef.current.setTool("Scroll");
      } catch (_) {
        try { dwvAppRef.current.setTool("ZoomAndPan"); } catch (_) {}
      }
      return;
    }
    try {
      dwvAppRef.current.setTool(toolName);
      activeToolRef.current = toolName;
      setActiveTool(toolName);
    } catch (e) {}
  }, [loaded, seriesScrollEnabled, canScroll]);

  // Wheel handling:
  // - Scroll tool + multi-file series (no volume): step slices
  // - ZoomAndPan: pinch-only zoom (trackpad pinch = wheel+ctrlKey; touch pinch
  //   uses dwv touch handlers). Plain mouse/trackpad scroll must not zoom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !loaded) return undefined;

    let lastStepAt = 0;
    const handleWheel = (e) => {
      const seriesScrollActive =
        seriesScrollEnabled
        && !volumeMode
        && !canScroll
        && activeTool === "Scroll"
        && typeof onSeriesScrollRef.current === "function";

      if (seriesScrollActive) {
        e.preventDefault();
        // Capture phase + stopImmediatePropagation: dwv's ZoomAndPan wheel handler
        // lives on the canvas inside this div, so it would otherwise zoom the image
        // on the same gesture that steps slices.
        e.stopImmediatePropagation();
        e.stopPropagation();
        const now = Date.now();
        if (now - lastStepAt < 60) return;
        lastStepAt = now;
        onSeriesScrollRef.current(e.deltaY > 0);
        return;
      }

      // Pinch-only: browsers fire trackpad pinch as wheel with ctrlKey.
      // Block plain scroll so dwv ZoomAndPan does not treat it as zoom.
      if (activeTool === "ZoomAndPan" && !e.ctrlKey) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    };

    const options = { passive: false, capture: true };
    el.addEventListener("wheel", handleWheel, options);
    return () => el.removeEventListener("wheel", handleWheel, options);
  }, [seriesScrollEnabled, activeTool, loaded, viewerReady, volumeMode, canScroll]);

  const selectShape = useCallback((shapeName) => {
    if (!dwvAppRef.current || !loaded) return;
    activeShapeRef.current = shapeName;
    setActiveShape(shapeName);
    try {
      dwvAppRef.current.setTool("Draw");
      dwvAppRef.current.setToolFeatures({ shapeName });
      activeToolRef.current = "Draw";
      setActiveTool("Draw");
    } catch (e) {}
  }, [loaded]);

  const applyPreset = useCallback((presetName) => {
    if (!dwvAppRef.current || !loaded) return;
    try {
      const vc = vcRef.current;
      if (vc) vc.setWindowLevelPreset(presetName);
    } catch (e) {}
  }, [loaded]);

  const resetView = useCallback(() => {
    if (!dwvAppRef.current || !loaded) return;
    try { dwvAppRef.current.resetZoomPan(); } catch (e) {}
  }, [loaded]);

  const toggleOrientation = useCallback(() => {
    if (!volumeMode && !canScroll) {
      toast.error("Open Coronal or Sagittal from the left panel to build a volume");
      return;
    }
    const nextOri = orientation === "axial" ? "coronal" : orientation === "coronal" ? "sagittal" : "axial";
    applyOrientation(nextOri);
  }, [orientation, applyOrientation, volumeMode, canScroll]);

  const isMultiFrameReady = hasMultiFrame || forceShowCine || (knownNumFrames && knownNumFrames > 1);
  const showFilmstrip = !hideFilmstrip && !hidePlaybackBar && isMultiFrameReady;
  const showCompactCine = hideFilmstrip && !hidePlaybackBar && isMultiFrameReady;
  const effectiveTotal = viewExtent && viewExtent > 1
    ? viewExtent
    : (totalFrames > 1 ? totalFrames : (knownNumFrames || 1));

  // Auto-start looping cine for fluoro-style studies once the viewer is ready.
  // The view's frame geometry can lag a few frames behind `loaded`, so poll
  // briefly instead of starting a cine that has nothing to scroll through.
  useEffect(() => {
    if (!autoPlayCine || !hideFilmstrip) return undefined;
    if (!loaded || !viewerReady) return undefined;
    if (!isMultiFrameReady) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timer = null;

    const tryStart = () => {
      if (cancelled) return;
      const geo = getPlaybackGeometry(vcRef.current, scrollDimRef.current);
      if (geo && geo.total > 1) {
        startCine();
        return;
      }
      attempts += 1;
      if (attempts > 20) return;
      timer = window.setTimeout(tryStart, 100);
    };

    timer = window.setTimeout(tryStart, 0);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [autoPlayCine, hideFilmstrip, loaded, viewerReady, isMultiFrameReady, startCine, fileUrl]);

  // Re-fit viewer when filmstrip mounts or layout shifts (avoids 0-size canvas errors)
  useEffect(() => {
    if (!loaded || !viewerReady || !dwvAppRef.current || !containerRef.current) return undefined;
    const id = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el || el.clientWidth <= 0 || el.clientHeight <= 0) return;
      try {
        dwvAppRef.current?.onResize();
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [loaded, viewerReady, showFilmstrip, showCompactCine]);

  const handleFilmstripFrame = useCallback((frame) => {
    stopCine();
    goToFrame(frame);
  }, [stopCine, goToFrame]);

  const handleToggleFullscreen = useCallback(async () => {
    const el = fullscreenRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen failed:", e);
    }
  }, [fullscreenRef]);

  const handleDownload = useCallback(async () => {
    if (!fileId && !fileUrl) return;
    setDownloading(true);
    try {
      let blob;
      if (fileUrl?.startsWith("blob:")) {
        blob = await fetch(fileUrl).then((r) => r.blob());
      } else if (fileId != null && seriesSliceIdx != null) {
        const res = await axios.get(
          `${API}/dicom/series/${fileId}/slice/${seriesSliceIdx}/raw`,
          { responseType: "blob" },
        );
        blob = res.data;
      } else if (fileId) {
        const res = await axios.get(`${API}/files/${fileId}/serve`, { responseType: "blob" });
        blob = res.data;
      } else {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error("fetch failed");
        blob = await res.blob();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "download.dcm";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  }, [fileId, fileUrl, fileName, seriesSliceIdx]);

  const handleToggleCine = useCallback(() => {
    // Fluoro mode: Cine button toggles playback instead of the filmstrip.
    if (hideFilmstrip) {
      if (isPlaying) stopCine();
      else {
        if (totalFramesRef.current < 2) {
          const fallback = knownNumFrames && knownNumFrames > 1 ? knownNumFrames : 96;
          setHasMultiFrame(true);
          setTotalFrames(fallback);
          totalFramesRef.current = fallback;
        }
        startCine();
      }
      return;
    }
    if (hasMultiFrame || forceShowCine) {
      setForceShowCine(false);
      setHasMultiFrame(false);
      stopCine();
      return;
    }
    setForceShowCine(true);
    if (totalFramesRef.current < 2) {
      const fallback = knownNumFrames && knownNumFrames > 1 ? knownNumFrames : 96;
      setTotalFrames(fallback);
      totalFramesRef.current = fallback;
    }
  }, [hideFilmstrip, isPlaying, hasMultiFrame, forceShowCine, stopCine, startCine, knownNumFrames]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenRef.current);
      requestAnimationFrame(() => {
        try {
          dwvAppRef.current?.onResize();
        } catch (_) {}
      });
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [fullscreenRef]);

  return (
    <div
      ref={externalFullscreenRef ? undefined : rootRef}
      className="flex flex-col h-full min-h-0 gap-1.5"
      data-testid="dicom-viewer"
    >
      {/* Toolbar above black viewer — excluded from sidebar height match */}
      <div
        className="flex items-center gap-1 flex-wrap px-1 min-h-10 flex-shrink-0"
        data-testid="dicom-toolbar"
      >
        {canWindowLevel && (
          <ToolBtn active={activeTool === "WindowLevel"} onClick={() => selectTool("WindowLevel")} label="W/L" testId="tool-windowlevel" />
        )}
        <ToolBtn active={activeTool === "ZoomAndPan"} onClick={() => selectTool("ZoomAndPan")} label="Zoom/Pan" testId="tool-zoompan" />
        {(canScroll || seriesScrollEnabled) && (
          <ToolBtn active={activeTool === "Scroll"} onClick={() => selectTool("Scroll")} label="Scroll" testId="tool-scroll" />
        )}

        {!hideDrawTools && (
          <>
            <div className="w-px h-6 bg-border mx-1" />

            <ToolBtn active={activeTool === "Draw" && activeShape === "Ruler"} onClick={() => selectShape("Ruler")} label="Ruler" testId="tool-ruler" />
            <ToolBtn active={activeTool === "Draw" && activeShape === "Protractor"} onClick={() => selectShape("Protractor")} label="Angle" testId="tool-protractor" />
            <ToolBtn active={activeTool === "Draw" && activeShape === "Ellipse"} onClick={() => selectShape("Ellipse")} label="Ellipse" testId="tool-ellipse" />
            <ToolBtn active={activeTool === "Draw" && activeShape === "Rectangle"} onClick={() => selectShape("Rectangle")} label="Rect" testId="tool-rectangle" />
            <ToolBtn active={activeTool === "Draw" && activeShape === "Arrow"} onClick={() => selectShape("Arrow")} label="Arrow" testId="tool-arrow" />
            <ToolBtn active={activeTool === "Draw" && activeShape === "Circle"} onClick={() => selectShape("Circle")} label="Circle" testId="tool-circle" />
          </>
        )}

        <div className="w-px h-6 bg-border mx-1" />

        {presetNames.length > 0 && (
          <select
            data-testid="wl-preset-select"
            className="h-7 px-2 text-xs bg-white border border-[var(--xl-line)] rounded text-foreground"
            onChange={(e) => applyPreset(e.target.value)}
            defaultValue=""
          >
            <option value="" disabled>Presets</option>
            {presetNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <ToolBtn onClick={resetView} label="Reset" testId="tool-reset" />
        {/* {!hideOrientationTool && (
          <ToolBtn onClick={toggleOrientation} label={orientation.charAt(0).toUpperCase() + orientation.slice(1)} testId="tool-orientation" />
        )} */}
        {/* <ToolBtn
          active={hideFilmstrip ? isPlaying : (hasMultiFrame || forceShowCine)}
          onClick={handleToggleCine}
          label="Cine"
          testId="tool-cine"
        /> */}
        {fileId && exportVideo && isMultiFrameReady && (
          <ToolBtn
            onClick={() => setExportOpen(true)}
            label="Export Video"
            testId="tool-export-video"
          />
        )}
        <ToolBtn active={showTags} onClick={() => setShowTags(!showTags)} label="Tags" testId="tool-tags" />
        <button
          type="button"
          data-testid="tool-fullscreen"
          onClick={handleToggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          className="h-7 px-2.5 text-xs rounded transition-colors bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary flex items-center gap-1.5 ml-auto"
        >
          {isFullscreen ? (
            <Minimize className="w-3.5 h-3.5" />
          ) : (
            <Maximize className="w-3.5 h-3.5" />
          )}
          <span className="hidden sm:inline">{isFullscreen ? "Exit" : "Fullscreen"}</span>
        </button>
      </div>

      {/* Black viewer — this height matches the left sidebar */}
      <div className="flex-1 flex gap-3 min-h-0">
        <div className="flex-1 flex flex-col relative rounded-lg overflow-hidden bg-black border border-border min-h-0">
          {patientName ? (
            <div
              className="absolute top-2 left-2 z-10 pointer-events-none max-w-[70%] sm:max-w-[50%]"
              data-testid="dicom-patient-name"
            >
              <p
                className="text-xs sm:text-sm font-medium text-white/95 truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]"
                title={patientName}
              >
                {patientName}
              </p>
            </div>
          ) : null}

          {/* Loading overlay */}
          {loadError && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 px-6 text-center" data-testid="dicom-load-error">
              <p className="text-sm text-red-400 mb-1">Could not display this DICOM file</p>
              <p className="text-xs text-muted-foreground">{loadError}</p>
            </div>
          )}

          {(loading || awaitingSource) && !loadError && !hideLoadingOverlay && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80" data-testid="dicom-loading">
              {loading ? (
                <>
                  <div className="w-48 h-1.5 bg-secondary rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${loadProgress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {volumeMode
                      ? `Building ${orientation} volume (${volumeUrls?.length || 0} slices)… ${loadProgress}%`
                      : `Parsing DICOM in browser… ${loadProgress}%`}
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 mt-2">
                    {volumeMode
                      ? "One-time load for coronal / sagittal"
                      : "File download is complete — this step runs locally"}
                  </p>
                </>
              ) : (
                <>
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3" />
                  <p className="text-xs text-muted-foreground">Loading DICOM…</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-2">Downloading file</p>
                </>
              )}
            </div>
          )}

          {/* Empty state */}
          {!loaded && !loading && !awaitingSource && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center" data-testid="dicom-empty-state">
              <div className="w-16 h-16 rounded-2xl bg-secondary/40 flex items-center justify-center mb-3">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v6m0 0H3m6 0h12M3 9v10a2 2 0 0 0 2 2h4m-6-12V5m18 4v10a2 2 0 0 1-2 2h-4m6-12V5M9 21h6m-6 0H5m10 0h4" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground mb-1">No DICOM loaded</p>
              <p className="text-xs text-muted-foreground">Select a DICOM file from the list</p>
            </div>
          )}

          {/* DWV renders into this div */}
          <div
            id="dwv-layerGroup"
            ref={containerRef}
            className="flex-1 min-h-[120px] min-w-0"
            data-testid="dwv-layer-container"
          />

          {bottomBar ? (
            <div className="flex-shrink-0 relative z-10">
              {bottomBar}
            </div>
          ) : null}

          {showCompactCine && loaded && viewerReady && !bottomBar && (
            <div
              className="bg-zinc-950/95 border-t border-zinc-800 px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0 relative z-10"
              data-testid="fluoro-cine-controls"
            >
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded flex items-center justify-center"
                  onClick={goToFirstFrame}
                  aria-label="First frame"
                  data-testid="cine-first"
                >
                  <SkipBack className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded flex items-center justify-center"
                  onClick={() => stepFrame(false)}
                  aria-label="Previous frame"
                  data-testid="cine-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="h-8 w-8 text-primary hover:bg-primary/15 rounded flex items-center justify-center"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  data-testid="cine-play"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded flex items-center justify-center"
                  onClick={() => stepFrame(true)}
                  aria-label="Next frame"
                  data-testid="cine-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                <span className="text-[11px] text-zinc-300 w-10 text-right tabular-nums font-medium">
                  {currentFrame + 1}
                </span>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, effectiveTotal - 1)}
                  step={1}
                  value={currentFrame}
                  onChange={(e) => {
                    stopCine();
                    goToFrame(Number(e.target.value));
                  }}
                  className="flex-1 accent-primary"
                  data-testid="frame-slider"
                  aria-label="Frame position"
                />
                <span className="text-[11px] text-zinc-400 w-10 tabular-nums">{effectiveTotal}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-zinc-400 hidden sm:inline">FPS</span>
                <select
                  value={fps}
                  onChange={(e) => setFps(Number(e.target.value))}
                  className="h-8 text-[11px] bg-zinc-800 border border-zinc-600 rounded-md px-2 text-zinc-100 focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="cine-fps"
                >
                  {[5, 10, 15, 24, 30].map((v) => (
                    <option key={v} value={v} className="bg-zinc-900">{v}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Tags Panel */}
        {showTags && metadata && (
          <div className="w-72 flex-shrink-0 rounded-lg border border-border bg-card overflow-hidden" data-testid="dicom-tags-panel">
            <div className="p-3 border-b border-border">
              <h3 className="text-xs font-semibold">DICOM Tags</h3>
            </div>
            <div className="overflow-auto h-[calc(100%-40px)] p-2">
              <div className="space-y-0.5">
                {Object.entries(metadata).map(([key, val]) => {
                  const display = typeof val === "object" && val !== null && val.value !== undefined ? val.value : val;
                  return (
                    <div key={key} className="flex text-[11px] py-1 px-1.5 rounded hover:bg-secondary/50" data-testid={`dicom-tag-${key}`}>
                      <span className="font-mono text-primary w-24 flex-shrink-0">{key}</span>
                      <span className="text-muted-foreground truncate">{String(display)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Anonymize / Export dialogs */}
      {/* <AnonymizeDialog
        open={anonOpen}
        onOpenChange={setAnonOpen}
        fileId={fileId}
        fileName={fileName}
      /> */}
      <ExportVideoDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        fileId={fileId}
        fileName={fileName}
        sliceIndex={seriesSliceIdx ?? currentFrame ?? 0}
      />
      <ExportFramesDialog
        open={extractOpen}
        onOpenChange={setExtractOpen}
        fileId={fileId}
        fileName={fileName}
        selectedFrames={selectedFrames}
        seriesSliceIdx={seriesSliceIdx}
      />

      {showFilmstrip && loaded && viewerReady && (
        <FrameFilmstrip
          getFrameThumbnail={getFrameThumbnail}
          totalFrames={effectiveTotal}
          currentFrame={currentFrame}
          selectedFrames={selectedFrames}
          onCurrentFrameChange={handleFilmstripFrame}
          onSelectionChange={setSelectedFrames}
          onExtract={() => setExtractOpen(true)}
          isPlaying={isPlaying}
          onTogglePlay={togglePlay}
          onStepFrame={stepFrame}
          onGoToFirst={goToFirstFrame}
          fps={fps}
          onFpsChange={setFps}
        />
      )}
    </div>
  );
}

function ToolBtn({ active, onClick, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`h-7 px-2.5 text-xs rounded transition-colors ${
        active ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary"
      }`}>
      {label}
    </button>
  );
}
